// No need to import a separate CSS file
// import './Navbar.css';

import React, { useState, useEffect } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { FaBars } from '@react-icons/all-files/fa/FaBars';
import { MdClose } from '@react-icons/all-files/md/MdClose';
import Logo from '../../asset/baws-logo.png';
import NotificationCenter from '../NotificationCenter/NotificationCenter';
import { useCuratorRole } from '../../hooks/useCuratorRole';

// Define navigation links in an array to stay DRY
const navLinks = [
  { to: '/', text: 'Home' },
  { to: '/about', text: 'About Us' },
];

const Navbar = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [hasShadow, setHasShadow] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(false);
  const user = useSelector((state) => state.userReducer);
  // Curators (Auth custom claim) also get a link to the feed curation screens.
  const { curator } = useCuratorRole();
  const links = curator
    ? [...navLinks, { to: '/curate', text: 'Curate' }]
    : navLinks;

  // Effect for adding a shadow on scroll
  useEffect(() => {
    const handleScroll = () => {
      setHasShadow(window.pageYOffset > 20);
    };
    window.addEventListener('scroll', handleScroll);

    // Programmatically track OS dark mode
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    setIsDarkMode(mq.matches);
    const themeHandler = (e) => setIsDarkMode(e.matches);
    mq.addEventListener('change', themeHandler);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      mq.removeEventListener('change', themeHandler);
    };
  }, []);

  // The bars never hide while the menu is open (useHideBarsOnScroll).
  useEffect(() => {
    const root = document.documentElement;
    if (isMobileMenuOpen) root.dataset.navMenu = 'open';
    else delete root.dataset.navMenu;
    return () => delete root.dataset.navMenu;
  }, [isMobileMenuOpen]);

  const closeMobileMenu = () => setIsMobileMenuOpen(false);

  // Function to determine NavLink classes for active state
  const getNavLinkClass = ({ isActive }) => {
    const baseClasses =
      'px-3 py-2 rounded-lg text-sm font-medium no-underline transition-colors duration-200';
    return isActive
      ? `${baseClasses} bg-indigo-50 text-indigo-800 font-semibold`
      : `${baseClasses} text-slate-700 hover:bg-slate-100 hover:text-slate-900`;
  };

  // A self-contained component for authentication buttons
  const AuthButtons = () => (
    <div className="flex items-center gap-2">
      {user.isLoggedIn ? (
        <NavLink
          to="/profile"
          className="px-4 py-2 text-sm font-semibold text-white no-underline bg-blue-600 rounded-lg shadow-sm hover:bg-blue-700 transition-all duration-300"
        >
          {user.username}
        </NavLink>
      ) : (
        <>
          <Link
            to="/login"
            className="px-4 py-2 text-sm font-semibold text-slate-800 no-underline border border-slate-200 rounded-lg hover:bg-slate-50 transition-all duration-300"
          >
            Log in
          </Link>
          <Link
            to="/signup"
            className="px-4 py-2 text-sm font-semibold text-white no-underline bg-blue-600 rounded-lg shadow-sm hover:bg-blue-700 transition-all duration-300"
          >
            Sign up
          </Link>
        </>
      )}
    </div>
  );

  return (
    <header
      data-hide-bar="top"
      className={`fixed top-0 left-0 w-full bg-white z-50 transition-shadow duration-300 ${
        hasShadow ? 'shadow-md' : ''
      }`}
      style={{
        paddingTop: window.isNativeApp
          ? 'env(safe-area-inset-top, 32px)'
          : '0px',
        // Status-bar strip that stays when the bar slides away (index.css)
        '--bars-inset': window.isNativeApp
          ? 'env(safe-area-inset-top, 32px)'
          : '0px',
      }}
    >
      {/* Background for the Safe Area Inset ONLY */}
      {window.isNativeApp && (
        <div
          className="absolute top-0 left-0 w-full"
          style={{
            height: 'env(safe-area-inset-top, 32px)',
            backgroundColor: isDarkMode ? '#0f172a' : '#ffffff',
          }}
        />
      )}

      {/* This container matches the width of your content below */}
      <div className="mx-auto max-w-7xl px-4 flex justify-between items-center h-16 border-b border-slate-100">
        {/* Logo */}
        <Link to="/" className="flex-shrink-0" onClick={closeMobileMenu}>
          <img src={Logo} alt="Baws Logo" className="h-10 w-auto" />
        </Link>

        {/* Desktop Navigation */}
        <nav className="hidden md:flex items-center gap-1">
          {links.map((link) => (
            <NavLink key={link.to} to={link.to} className={getNavLinkClass}>
              {link.text}
            </NavLink>
          ))}
        </nav>

        {/* Desktop Auth Buttons */}
        <div className="hidden md:flex items-center gap-4">
          <NotificationCenter />
          <AuthButtons />
        </div>

        {/* Mobile Menu Toggle */}
        <div className="md:hidden flex items-center gap-4">
          <NotificationCenter />
          <button
            className="text-2xl text-gray-700"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label="Toggle navigation"
          >
            {isMobileMenuOpen ? <MdClose /> : <FaBars />}
          </button>
        </div>
      </div>

      {/* Mobile Menu (Sliding Panel) */}
      <div
        className={`md:hidden absolute left-0 w-full bg-white shadow-lg transition-transform duration-300 ease-in-out ${
          isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
        style={{
          top: window.isNativeApp
            ? 'calc(4rem + env(safe-area-inset-top, 32px))'
            : '4rem',
        }}
      >
        <div className="p-6">
          <nav className="flex flex-col gap-5">
            {links.map((link) => (
              <NavLink
                key={link.to}
                to={link.to}
                className={getNavLinkClass}
                onClick={closeMobileMenu}
              >
                {link.text}
              </NavLink>
            ))}
          </nav>
          <hr className="my-6 border-gray-200" />
          <div onClick={closeMobileMenu}>
            <AuthButtons />
          </div>
        </div>
      </div>
    </header>
  );
};

export default Navbar;
